import { ActionReducerMap, createFeatureSelector, createSelector } from '@ngrx/store';

/** MODELS */
// MAP_TRIGGER import is required to make sure it is included in the library build
import { AppState, MAP_TRIGGER } from './app-state/app-state.models';
/** REDUCERS */
import * as fromAppState from './app-state/app-state.reducer';
import { DrpSearchConfig } from './config/config.models';
import * as fromConfig from './config/config.reducer';
import { GeoCoderState } from './geo-coder/geo-coder.models';
import * as fromGeoCoder from './geo-coder/geo-coder.reducers';
import { PaginationState } from './pagination/pagination.models';
import * as fromPage from './pagination/pagination.reducer';
import { DrpLocatorState } from './service-locator/service-locator.models';
import * as fromDrpLocator from './service-locator/service-locator.reducers';

export interface State {
  appState: AppState;
  config: DrpSearchConfig;
  drpLocatorState: DrpLocatorState;
  geoCoderState: GeoCoderState;
  paginationState: PaginationState;
}

export const reducers: ActionReducerMap<State> = {
  appState: fromAppState.reducer,
  config: fromConfig.reducer,
  drpLocatorState: fromDrpLocator.reducer,
  geoCoderState: fromGeoCoder.reducer,
  paginationState: fromPage.reducer
};

// Create the main feature selector
export const getFeatureState = createFeatureSelector<State>('drpSearch');

export const getConfig = createSelector(getFeatureState, (state: State) => state?.config);
export const getGeoApiUrl = createSelector(getConfig, fromConfig.getGeoApiUrl);
export const getclaimVendorLocationUrl = createSelector(
  getConfig,
  fromConfig.getclaimVendorLocationUrl
);

export const getAppState = createSelector(getFeatureState, (state: State) => state.appState);
export const getSearchType = createSelector(getAppState, fromAppState.getSearchType);
export const getCurrentAppState = createSelector(getAppState, fromAppState.getAppState);
export const getMapRedrawTriggerState = createSelector(
  getAppState,
  fromAppState.getMapRedrawTrigger
);

export const getDrpLocatorState = createSelector(
  getFeatureState,
  (state: State) => state.drpLocatorState
);
export const getSearchContext = createSelector(getDrpLocatorState, fromDrpLocator.getSearchContext);
export const getSearchResults = createSelector(getDrpLocatorState, fromDrpLocator.getSearchResults);
export const getSearchedValue = createSelector(getDrpLocatorState, fromDrpLocator.getSearchedValue);
export const getDrpLoading = createSelector(getDrpLocatorState, fromDrpLocator.isLoading);
export const getLastSuccessfulSearchedValue = createSelector(
  getDrpLocatorState,
  fromDrpLocator.getLastSuccessfulSearchValue
);
export const getDrpError = createSelector(getDrpLocatorState, fromDrpLocator.getError);

export const getGoogleState = createSelector(
  getFeatureState,
  (state: State) => state.geoCoderState
);
export const getGeoApiLoading = createSelector(getGoogleState, fromGeoCoder.isLoading);
export const getPredictions = createSelector(getGoogleState, fromGeoCoder.getPredictions);
export const getUserSelectedChoice = createSelector(
  getGoogleState,
  fromGeoCoder.getUserSelectedChoice
);
export const getFullAddress = createSelector(getGoogleState, fromGeoCoder.getfullAddress);
export const getSelectedShop = createSelector(getGoogleState, fromGeoCoder.getSelectedShop);
export const getGoogleError = createSelector(getGoogleState, fromGeoCoder.getError);

export const getPaginationState = createSelector(
  getFeatureState,
  (state: State) => state.paginationState
);
export const getTotalPages = createSelector(getPaginationState, fromPage.getTotalPages);
export const getPageItems = createSelector(getPaginationState, fromPage.getPageItems);
export const getCurrentPage = createSelector(getPaginationState, fromPage.getCurrentPage);

export const getLoading = createSelector(getDrpLoading, getGeoApiLoading, (drp, geo) => {
  return drp || geo;
});

export const getErrors = createSelector(getDrpError, getGoogleError, (drpError, googleError) => {
  const errors = [];
  if (drpError) {
    errors.push(drpError);
  } else if (googleError) {
    errors.push(googleError);
  }
  return errors;
});
